import { __spread } from 'tslib';
import { Pipe, Component, EventEmitter, forwardRef, HostListener, Input, IterableDiffers, Output, ViewChild, NgZone, NgModule } from '@angular/core';
import { FormBuilder, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, mergeMap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TagsModule } from 'ecova-tags';

var MultiSelectSearchFilter = /** @class */ (function () {
    function MultiSelectSearchFilter() {
        this._searchCache = {};
        this._searchCacheInclusive = {};
    }
    MultiSelectSearchFilter.prototype.transform = function (options, str, limit, renderLimit) {
        if (limit === void 0) { limit = 0; }
        if (renderLimit === void 0) { renderLimit = 0; }
        str = (str || '').toLowerCase();
        if (options !== this._lastOptions) {
            this._lastOptions = options;
            this._searchCache = {};
            this._searchCacheInclusive = {};
        }
        var isUnderLimit = options.length <= limit;
        if (this._searchCache[str]) {
            return isUnderLimit ? this._searchCache[str] : this._limitRenderedItems(this._searchCache[str], renderLimit);
        }
        var prevStr = str.slice(0, -1);
        var prevResults = this._searchCache[prevStr];
        if (prevResults) {
            var prevInclusiveOrIdx = this._searchCacheInclusive[prevStr];
            if (prevInclusiveOrIdx === true) {
                options = prevResults;
            }
            else if (typeof prevInclusiveOrIdx === 'number') {
                options = __spread(prevResults, options.slice(prevInclusiveOrIdx));
            }
        }
        var optsLength = options.length;
        var maxFound = limit > 0 ? Math.min(limit, optsLength) : optsLength;
        var filteredOpts = [];
        var regexp = new RegExp(this._escapeRegExp(str), 'i');
        var matchPredicate = (function (option) { return regexp.test(option.name); });
        var getChildren = (function (option) { return options.filter((function (child) { return child.parentId === option.id; })); });
        var getParent = (function (option) { return options.find((function (parent) { return option.parentId === parent.id; })); });
        var i = 0;
        var founded = 0;
        for (; i < optsLength && founded < maxFound; ++i) {
            var option = options[i];
            var directMatch = regexp.test(option.name);
            if (directMatch) {
                filteredOpts.push(option);
                founded++;
                continue;
            }
            if (typeof (option.parentId) === 'undefined') {
                var childrenMatch = getChildren(option).some(matchPredicate);
                if (childrenMatch) {
                    filteredOpts.push(option);
                    founded++;
                    continue;
                }
            }
            if (typeof (option.parentId) !== 'undefined') {
                var parentMatch = matchPredicate(getParent(option));
                if (parentMatch) {
                    filteredOpts.push(option);
                    founded++;
                    continue;
                }
            }
        }
        this._searchCache[str] = filteredOpts;
        this._searchCacheInclusive[str] = i === optsLength || i + 1;
        return isUnderLimit ? filteredOpts : this._limitRenderedItems(filteredOpts, renderLimit);
    };
    MultiSelectSearchFilter.prototype._limitRenderedItems = function (items, limit) {
        return items.length > limit && limit > 0 ? items.slice(0, limit) : items;
    };
    MultiSelectSearchFilter.prototype._escapeRegExp = function (str) {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    };
    return MultiSelectSearchFilter;
}());
MultiSelectSearchFilter.decorators = [
    { type: Pipe, args: [{
                name: 'searchFilter'
            },] },
];
var MULTISELECT_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef((function () { return MultiSelectDropDown; })),
    multi: true
};
var MultiSelectDropDown = /** @class */ (function () {
    function MultiSelectDropDown(fb, searchFilter, differs, _ngZone) {
        this.fb = fb;
        this.searchFilter = searchFilter;
        this._ngZone = _ngZone;
        this.filterControl = this.fb.control('');
        this.disabled = false;
        this.onLazyLoad = new EventEmitter();
        this.onSearch = this.filterControl.valueChanges
            .pipe(debounceTime(300))
            .pipe(distinctUntilChanged())
            .pipe(mergeMap((function (val) { return of(val); })));
        this.onTabPress = new EventEmitter();
        this.onEnterPress = new EventEmitter();
        this.destroyed$ = new Subject();
        this.model = [];
        this.prevModel = [];
        this.numSelected = 0;
        this.renderItems = true;
        this.checkAllSearchRegister = new Set();
        this.checkAllStatus = false;
        this.loadedValueIds = [];
        this.defaultSettings = {
            pullRight: false,
            checkedStyle: 'checkboxes',
            showCheckAll: false,
            showUncheckAll: false,
            fixedTitle: false,
            dynamicTitleMaxItems: 0,
            maxHeight: '300px',
            isLazyLoad: true,
            stopScrollPropagation: true,
            loadViewDistance: 0,
            selectAddedValues: false
        };
        this.defaultTexts = {
            checkAll: 'Check all',
            uncheckAll: 'Uncheck all',
            checked: 'Selected',
            checkedPlural: 'Selected',
            searchPlaceholder: 'Search',
            defaultTitle: 'Select',
            allSelected: 'All selected',
        };
        this._isVisible = false;
        this._workerDocClicked = false;
        this._keyboardControlIdx = -1;
        this.onModelChange = (function (_) { });
        this.onModelTouched = (function () { });
        this.differ = differs.find([]).create(null);
        this.settings = this.defaultSettings;
        this.texts = this.defaultTexts;
    }
    MultiSelectDropDown.prototype.onClick = function (target) {
        if (!this.isVisible) {
            return;
        }
        var parentFound = false;
        while (target != null && !parentFound) {
            if (target === this.containerElement.nativeElement || target.classList.contains('ecova-multiselect-dropdown')) {
                parentFound = true;
            }
            target = target.parentElement;
        }
        if (!parentFound) {
            this.isVisible = false;
        }
    };
    Object.defineProperty(MultiSelectDropDown.prototype, "isVisible", {
        get: function () {
            return this._isVisible;
        },
        set: function (val) {
            this._isVisible = val;
            this._workerDocClicked = val ? false : this._workerDocClicked;
        },
        enumerable: true,
        configurable: true
    });
    MultiSelectDropDown.prototype.getItemStyle = function (option) {
        if (!option.isLabel) {
            return { 'cursor': 'pointer' };
        }
    };
    MultiSelectDropDown.prototype.ngOnInit = function () {
        this.settings = Object.assign(this.defaultSettings, this.settings);
        this.texts = Object.assign(this.defaultTexts, this.texts);
        this.title = this.texts.defaultTitle || '';
    };
    MultiSelectDropDown.prototype.ngOnChanges = function (changes) {
        if (changes['options']) {
            this.options = this.options || [];
            this.parents = this.options
                .filter((function (option) { return typeof option.parentId === 'number'; }))
                .map((function (option) { return option.parentId; }));
            this.model = this.model.filter((function (elem) {
                return changes.options.currentValue.find((function (option) { return option.id === elem; })) !== undefined;
            }));
            if (this.texts) {
                this.updateTitle();
            }
            this.fireModelChange();
        }
        if (changes['texts'] && !changes['texts'].isFirstChange()) {
            this.updateTitle();
        }
    };
    MultiSelectDropDown.prototype.ngOnDestroy = function () {
        this.destroyed$.next();
    };
    MultiSelectDropDown.prototype.applyFilters = function (options, value) {
        return (this.searchFilter.transform(options, value));
    };
    MultiSelectDropDown.prototype.fireModelChange = function () {
        if (this.model !== this.prevModel) {
            this.prevModel = this.model;
            this.onModelChange(this.model);
            this.onModelTouched();
        }
    };
    MultiSelectDropDown.prototype.writeValue = function (value) {
        if (value !== undefined && value !== null) {
            this.model = Array.isArray(value) ? value : [value];
        }
        else {
            this.model = [];
        }
    };
    MultiSelectDropDown.prototype.registerOnChange = function (fn) {
        this.onModelChange = fn;
    };
    MultiSelectDropDown.prototype.registerOnTouched = function (fn) {
        this.onModelTouched = fn;
    };
    MultiSelectDropDown.prototype.setDisabledState = function (isDisabled) {
        this.disabled = isDisabled;
    };
    MultiSelectDropDown.prototype.ngDoCheck = function () {
        var changes = this.differ.diff(this.model);
        if (changes) {
            this.updateNumSelected();
            this.updateTitle();
        }
    };
    MultiSelectDropDown.prototype.validate = function (_c) {
        return (this.model && this.model.length) ? null : {
            required: {
                valid: false,
            },
        };
    };
    MultiSelectDropDown.prototype.registerOnValidatorChange = function (_fn) {
        throw new Error('Method not implemented.');
    };
    MultiSelectDropDown.prototype.clearSearch = function (event) {
        if (event.stopPropagation) {
            event.stopPropagation();
        }
        this.filterControl.setValue('');
    };
    MultiSelectDropDown.prototype.toggleDropdown = function () {
        this.isVisible = !this.isVisible;
        if (this.isVisible) {
            var input_1 = this.searchInput.nativeElement;
            this._ngZone.run((function () {
                setTimeout((function () {
                    input_1.focus();
                }), 0);
            }));
        }
    };
    MultiSelectDropDown.prototype.setDropdown = function (val) {
        this.isVisible = val;
        if (val) {
            var input_2 = this.searchInput.nativeElement;
            this._ngZone.run((function () {
                setTimeout((function () {
                    input_2.focus();
                }), 0);
            }));
        }
    };
    MultiSelectDropDown.prototype.isSelected = function (option) {
        return this.model && this.model.indexOf(option.id) > -1;
    };
    MultiSelectDropDown.prototype.setSelected = function (_event, option) {
        var _this = this;
        if (option.isLabel) {
            return;
        }
        if (_event && _event.stopPropagation) {
            _event.stopPropagation();
        }
        var index = this.model.indexOf(option.id);
        if (index > -1) {
            this.model.splice(index, 1);
            var parentIndex = option.parentId && this.model.indexOf(option.parentId);
            if (parentIndex > -1) {
                this.model.splice(parentIndex, 1);
            }
            else if (this.parents.indexOf(option.id) > -1) {
                this.options.filter((function (child) { return _this.model.indexOf(child.id) > -1 && child.parentId === option.id; }))
                    .forEach((function (child) { return _this.model.splice(_this.model.indexOf(child.id), 1); }));
            }
        }
        else {
            this.model.push(option.id);
            if (option.parentId) {
                var children = this.options.filter((function (child) { return child.id !== option.id && child.parentId === option.parentId; }));
                if (children.every((function (child) { return _this.model.indexOf(child.id) > -1; }))) {
                    this.model.push(option.parentId);
                }
            }
            else if (this.parents.indexOf(option.id) > -1) {
                var children = this.options.filter((function (child) { return _this.model.indexOf(child.id) < 0 && child.parentId === option.id; }));
                children.forEach((function (child) { return _this.model.push(child.id); }));
            }
        }
        this.model = this.model.slice();
        this.fireModelChange();
    };
    MultiSelectDropDown.prototype.updateNumSelected = function () {
        var _this = this;
        this.numSelected = this.model.filter((function (id) { return _this.parents.indexOf(id) < 0; })).length || 0;
    };
    MultiSelectDropDown.prototype.updateTitle = function () {
        var _this = this;
        if (this.numSelected === 0 || this.settings.fixedTitle) {
            this.title = (this.texts) ? this.texts.defaultTitle : '';
        }
        else if (this.settings.displayAllSelectedText && this.model.length === this.options.length) {
            this.title = (this.texts) ? this.texts.allSelected : '';
        }
        else if (this.settings.dynamicTitleMaxItems && this.settings.dynamicTitleMaxItems >= this.numSelected) {
            var useOptions = this.options;
            this.title = useOptions
                .filter((function (option) { return _this.model.indexOf(option.id) > -1; }))
                .map((function (option) { return option.name; }))
                .join(', ');
        }
        else {
            this.title = this.numSelected
                + ' '
                + (this.numSelected === 1 ? this.texts.checked : this.texts.checkedPlural);
        }
    };
    MultiSelectDropDown.prototype.searchFilterApplied = function () {
        return this.filterControl.value && this.filterControl.value.length > 0;
    };
    MultiSelectDropDown.prototype.addChecks = function (options) {
        var checkedOptions = options
            .filter((function (option) {
            if (this.model.indexOf(option.id) === -1) {
                return true;
            }
            return false;
        }).bind(this)).map((function (option) { return option.id; }));
        this.model = this.model.concat(checkedOptions);
    };
    MultiSelectDropDown.prototype.checkScrollPosition = function (ev) {
        var scrollTop = ev.target.scrollTop;
        var scrollHeight = ev.target.scrollHeight;
        var scrollElementHeight = ev.target.clientHeight;
        var roundingPixel = 1;
        var gutterPixel = 1;
        if (scrollTop >= scrollHeight - (1 + this.settings.loadViewDistance) * scrollElementHeight - roundingPixel - gutterPixel) {
            this.load();
        }
    };
    MultiSelectDropDown.prototype.checkScrollPropagation = function (ev, element) {
        var scrollTop = element.scrollTop;
        var scrollHeight = element.scrollHeight;
        var scrollElementHeight = element.clientHeight;
        if ((ev.deltaY > 0 && scrollTop + scrollElementHeight >= scrollHeight) || (ev.deltaY < 0 && scrollTop <= 0)) {
            ev = ev || window.event;
            ev.returnValue = false;
            if (ev.preventDefault) {
                ev.preventDefault();
            }
        }
    };
    MultiSelectDropDown.prototype.menuKeyDown = function (ev, menu, search) {
        var checkboxes = menu.querySelectorAll('.list-item-container ul li a');
        switch (ev.keyCode) {
            case 38:
                if (this._keyboardControlIdx !== -1) {
                    this._keyboardControlIdx--;
                    if (this._keyboardControlIdx === -1) {
                        search.focus();
                    }
                    else {
                        checkboxes[this._keyboardControlIdx].focus();
                    }
                    ev.preventDefault();
                }
                break;
            case 40:
                if (this._keyboardControlIdx < checkboxes.length - 1) {
                    this._keyboardControlIdx++;
                    checkboxes[this._keyboardControlIdx].focus();
                    ev.preventDefault();
                }
                break;
            case 32:
            case 37:
            case 39:
                if (this._keyboardControlIdx > -1) {
                    this.setSelected(null, this.options[this._keyboardControlIdx]);
                    ev.preventDefault();
                }
                break;
            default: break;
        }
    };
    MultiSelectDropDown.prototype.containerKeyDown = function (ev) {
        if (ev.keyCode === 9) {
            this.onTabPress.emit();
            ev.preventDefault();
        }
        else if (ev.keyCode === 13) {
            this.onEnterPress.emit();
            ev.preventDefault();
        }
    };
    MultiSelectDropDown.prototype.load = function () {
        this.onLazyLoad.emit({
            length: this.options.length,
            filter: this.filterControl.value,
            checkAllSearches: this.checkAllSearchRegister,
            checkAllStatus: this.checkAllStatus
        });
    };
    MultiSelectDropDown.prototype.clearSearchText = function () {
        this.filterControl.setValue('');
    };
    return MultiSelectDropDown;
}());
MultiSelectDropDown.decorators = [
    { type: Component, args: [{
                selector: 'ecova-multi-select-drop-down',
                template: "<div #containerElement class=\"dropdown\" (keydown)=\"containerKeyDown($event)\">\n  <div class=\"toggle\" (click)=\"toggleDropdown()\">\n    {{title}}\n    <i class=\"fas fa-chevron-up\" [ngClass]=\"{'fa-chevron-up':isVisible, 'fa-chevron-down':!isVisible}\" aria-hidden=\"true\"></i>\n  </div>\n\n  <div #menu class=\"menu\" [hidden]=\"!isVisible\" (keydown)=\"menuKeyDown($event, menu, searchInput)\">\n    <div class=\"tag-container\" *ngIf=\"model && model.length > 0\">\n      <ecova-tags [(selected)]=\"model\" [dataset]=\"options\" [addClass]=\"'ecova-multiselect-dropdown'\"></ecova-tags>\n    </div>\n\n    <div class=\"search-container\">\n      <input #searchInput type=\"text\" \n        class=\"search form-control ecova-input\" \n        placeholder=\"{{ texts.searchPlaceholder }}\" \n        aria-describedby=\"sizing-addon3\" \n        [formControl]=\"filterControl\" \n        autofocus>\n\n      <i class=\"fas fa-times clearSearch\" \n        aria-hidden=\"true\"\n        (click)=\"clearSearchText()\" \n        [hidden]=\"filterControl.value.length === 0\">\n      </i>\n    </div>\n\n    <div class=\"list-item-container\" *ngIf=\"options && options.length > 0\">\n      <ul #scroller [style.max-height]=\"settings.maxHeight\"\n        (scroll)=\"settings.isLazyLoad ? checkScrollPosition($event) : null\"\n        (wheel)=\"settings.stopScrollPropagation ? checkScrollPropagation($event, scroller) : null\">\n\n        <li *ngFor=\"let option of options\"\n          (click)=\"setSelected($event, option)\"\n          [ngStyle]=\"getItemStyle(option)\"\n          [ngClass]=\"option.classes\"\n          [class.dropdown-header]=\"option.isLabel\">\n\n          <input type=\"checkbox\" [checked]=\"isSelected(option)\"/>\n          <a href=\"javascript:;\" class=\"ecova-text\">{{option.name}}</a>  \n        </li>\n      </ul>\n    </div>\n  </div>\n</div>",
                styles: [".dropdown{background-color:#fff;border:1px solid #ccc;border-radius:3px;font-size:14px;color:#999;padding:5px 10px;position:relative}.dropdown .toggle i{display:inline-block;right:11px;position:absolute}.dropdown .menu .tag-container{margin-top:5px;padding-top:5px;border-top:1px dashed #ccc}.dropdown .menu .search-container{margin-top:5px;border-top:1px dashed #ccc;position:relative}.dropdown .menu .search-container input.search{margin-top:5px}.dropdown .menu .search-container i.clearSearch{position:absolute;right:13px;top:16px;cursor:pointer}.dropdown .menu .list-item-container{margin-top:25px}.dropdown .menu .list-item-container ul{list-style:none;padding:0;display:block;height:auto;overflow-y:auto}.dropdown .menu .list-item-container ul li{cursor:pointer;padding:5px 0}.dropdown .menu .list-item-container ul li:hover{background-color:#ccc}.dropdown .menu .list-item-container ul li a{text-decoration:none}.dropdown .menu .list-item-container ul li a:hover{color:#333}.dropdown .menu .list-item-container ul li input[type=checkbox]{display:none}.dropdown .menu .list-item-container ul li input[type=checkbox]+a:before{width:19px;height:19px;border:1px solid #ccc;content:\"\";color:#ccc;margin-right:15px;float:left}.dropdown .menu .list-item-container ul li input[type=checkbox]:checked+a:before{font-family:\"Font Awesome 5 Free\",FontAwesome;font-size:19px;line-height:19px;content:\"\\f00c\";color:#0099d8;letter-spacing:5px}"],
                providers: [MULTISELECT_VALUE_ACCESSOR, MultiSelectSearchFilter]
            },] },
];
MultiSelectDropDown.ctorParameters = function () { return [
    { type: FormBuilder },
    { type: MultiSelectSearchFilter },
    { type: IterableDiffers },
    { type: NgZone }
]; };
MultiSelectDropDown.propDecorators = {
    containerElement: [{ type: ViewChild, args: ['containerElement',] }],
    searchInput: [{ type: ViewChild, args: ['searchInput',] }],
    options: [{ type: Input }],
    settings: [{ type: Input }],
    texts: [{ type: Input }],
    disabled: [{ type: Input }],
    onLazyLoad: [{ type: Output }],
    onSearch: [{ type: Output }],
    onTabPress: [{ type: Output }],
    onEnterPress: [{ type: Output }],
    onClick: [{ type: HostListener, args: ['document: click', ['$event.target'],] }]
};
var MultiSelectDropDownModule = /** @class */ (function () {
    function MultiSelectDropDownModule() {
    }
    return MultiSelectDropDownModule;
}());
MultiSelectDropDownModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    ReactiveFormsModule,
                    TagsModule
                ],
                declarations: [
                    MultiSelectDropDown,
                    MultiSelectSearchFilter
                ],
                exports: [
                    FormsModule,
                    MultiSelectDropDown,
                    MultiSelectSearchFilter
                ]
            },] },
];

export { MultiSelectDropDownModule, MultiSelectDropDown, MultiSelectSearchFilter as ɵa };
//# sourceMappingURL=ecova-multi-select-drop-down.js.map
